import { useContext, useState, useCallback, useEffect } from "react";
import {
  Box,
  CssBaseline,
  Drawer,
  Typography,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { NavLink, useLocation } from "react-router-dom";
import {
  supplierSidebarItems,
  communitySidebarItems,
} from "./LayoutUtils/SidebarItems";
import WelcomePopup from "./Modals/WelcomePopup";
import UpdatePopup from "./Modals/UpdatePopup";
import PlatformTermsModal from "../../Components/PlatformTermsModal";
import { logoutHandler } from "../../Utils/auth";
import { apiGetRequest, apiPostRequest } from "../../Helpers/Requests";
import endpoints from "../../Helpers/Endpoints";
// ⚠️ TEMP DEV-PHASE PREVIEW — remove with the KYC preview seed.
import KycPreviewSwitcher from "../../Components/KycPreviewSwitcher";
import TopAppBar from "./TopAppBar";
import BottomNavMenu from "./BottomNavMenu";
import { AppContext } from "../../Context/AppContext";
import { layoutStyles, previewBadge, soonBadge } from "./styles/styles";
import { useTranslation } from "react-i18next";
import CustomTooltipBox from "../../Components/CustomTooltips/CustomTooltipBox";
import UpdateTos from "./BottomNavMenu/UpdateTos";
import { KEILOT_WHATSAPP_NUMBER } from "../../Constants/config";
import { KEILOT_MAIL_ADDRESS } from "../../Constants/config";
import SidebarHeader from "./SidebarHeader";
import emitter from "../../Utils/events/eventBus";
import AgentChat from "../../Components/AgentChat";
import AgentChatBoundary from "../../Components/AgentChat/AgentChatBoundary";

const CommunityLayout = ({
  children,
  title: customTitle,
  hideTitle = false,
  mobileTitleOnly = false,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const isBelowMd = useMediaQuery(theme.breakpoints.down("md"));
  const {
    sidebarData,
    isCommunityManagementRole,
    languages,
    updateLanguage,
    showWelcomePopup,
    setShowWelcomePopup,
    showUpdatePopup,
    setShowUpdatePopup,
    getStartedContent,
    userCommunities,
    fetchGetStartedContent,
  } = useContext(AppContext);

  const [drawerOpen, setDrawerOpen] = useState(true);
  const [activeScreen, setActiveScreen] = useState("");
  const [pageTitle, setPageTitle] = useState("");
  // Keilot's own terms come before anything else on screen: a person who has
  // not approved the current versions should not be using the system, and the
  // community-TOS dialog queues behind this rather than stacking on it.
  // The documents and whether an approval is owed come from the server — the
  // same tables members and Onboarding read, so approving in one app clears
  // it in the other.
  const [terms, setTerms] = useState(null);
  const [termsSettled, setTermsSettled] = useState(false);
  const [termsMode, setTermsMode] = useState(null);

  const loadTerms = useCallback(async () => {
    try {
      const { data } = await apiGetRequest(endpoints.platformTerms);
      setTerms(data);
      if (data?.acceptanceRequired) setTermsMode("accept");
    } catch (error) {
      // Nothing to show. Writes are still refused server-side while an
      // approval is owed, and that refusal loads the terms again.
      console.error("Failed to load the platform terms:", error);
    } finally {
      setTermsSettled(true);
    }
  }, []);

  useEffect(() => {
    loadTerms();
  }, [loadTerms]);

  useEffect(() => {
    // A write refused because an approval is owed (Requests' interceptor) —
    // a version published while the person was mid-session.
    emitter.on("platform-terms-required", loadTerms);
    return () => emitter.off("platform-terms-required", loadTerms);
  }, [loadTerms]);

  useEffect(() => {
    // The sidebar link opens the read-only view; an owed approval outranks it.
    const openTerms = () =>
      setTermsMode((current) => (current === "accept" ? current : "read"));
    emitter.on("open-platform-terms", openTerms);
    return () => emitter.off("open-platform-terms", openTerms);
  }, []);

  const acceptTerms = useCallback(async () => {
    await apiPostRequest(endpoints.acceptPlatformTerms);
    setTerms((current) => current && { ...current, acceptanceRequired: false });
    setTermsMode(null);
  }, []);

  // The community's own TOS waits until Keilot's terms are settled and
  // approved, then opens on its own.
  const termsCleared = termsSettled && termsMode !== "accept";

  const [tosUpdateDialog, setTosUpdateDialog] = useState({
    open: false,
    community: null,
  });
  const [remainingTosUpdates, setRemainingTosUpdates] = useState([]);

  // Manager-of-Managers view: a portfolio-level role whose sidebar has ONLY the
  // two top links (multi-community dashboard + Managers hub) and none of the
  // single-community pages. In prod `momView` comes from the backend.
  const isMoMView = sidebarData?.momView === true;
  const sidebarItems = isMoMView
    ? []
    : isCommunityManagementRole
      ? communitySidebarItems
      : supplierSidebarItems;

  // load 2 namespaces for pre-loading settings locales
  const { t } = useTranslation(
    "routes-and-main-menu",
    "bottom-menu-and-settings",
    "tooltip-sections",
  );

  const getPageTitle = useCallback(
    (pathname) => {
      if (hideTitle) return null;

      if (customTitle) return customTitle;

      const path = pathname.split("/").pop();
      if (!path) return "Dashboard";

      return path
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    [customTitle, hideTitle],
  );

  useEffect(() => {
    setPageTitle(getPageTitle(location.pathname));
  }, [location.pathname, getPageTitle]);

  useEffect(() => {
    if (isBelowMd) {
      setDrawerOpen(false);
      setActiveScreen("");
    } else {
      setDrawerOpen(true);
      setActiveScreen("");
    }
  }, [isBelowMd]);

  useEffect(() => {
    setActiveScreen("");
  }, [drawerOpen]);

  useEffect(() => {
    // Check for communitiesTosUpdates and open TOS dialog if present
    if (
      termsCleared &&
      sidebarData?.communitiesTosUpdates &&
      sidebarData.communitiesTosUpdates.length > 0
    ) {
      setTosUpdateDialog({
        open: true,
        community: sidebarData.communitiesTosUpdates[0],
      });
      setRemainingTosUpdates(sidebarData.communitiesTosUpdates);
    }
  }, [sidebarData, termsCleared]);

  // Notifications panel TOS CTA (notification #10) opens the layout-owned dialog
  useEffect(() => {
    const openTosDialog = () =>
      setTosUpdateDialog({ open: true, community: null });
    emitter.on("open-tos-update-dialog", openTosDialog);
    return () => emitter.off("open-tos-update-dialog", openTosDialog);
  }, []);

  const toggleDrawer = () => {
    if (isBelowMd) {
      setDrawerOpen((prev) => !prev);
    }
  };
  const closeDrawer = useCallback(() => {
    if (isBelowMd) {
      setDrawerOpen(false);
    }
  }, [isBelowMd]);

  const handleItemClick = (active) => {
    setActiveScreen(active);
  };

  const drawerWidth = isBelowMd ? 292 : 276;

  const {
    root,
    drawer,
    mainContent,
    pageTitleBox,
    pageTitleTextBox,
    pageTitleStyle,
    contentContainer,
    menuItem,
    menuItemText,
    sidebarContent,
    menuItemsContainer,
    bottomNavContainer,
    closeIcon,
    closeButtonContainer,
    mainContentBox,
  } = layoutStyles;

  useEffect(() => {
    closeDrawer();
  }, [location.pathname, closeDrawer]);

  // The Managers hub lives in SidebarHeader, not sidebarItems, but its page
  // title still needs the FE-first preview badge.
  const currentItem =
    sidebarItems.find((item) => location.pathname.includes(item.link)) ||
    (location.pathname.includes("/managers-hub")
      ? { id: "managers_hub", link: "/managers-hub", preview: true }
      : undefined);

  return (
    <>
      <PlatformTermsModal
        open={!!termsMode && !!terms?.documents?.length}
        mode={termsMode === "read" ? "read" : "accept"}
        documents={terms?.documents}
        updatedAt={terms?.updatedAt}
        onClose={() => setTermsMode(null)}
        onAccept={acceptTerms}
        onLogout={logoutHandler}
      />

      {/* ⚠️ TEMP DEV-PHASE PREVIEW — the KYC state switcher. Renders nothing
          unless `?kycPreview=1` was used. Remove at graduation. */}
      <KycPreviewSwitcher />
      <WelcomePopup
        open={showWelcomePopup}
        onClose={() => setShowWelcomePopup(false)}
        keilotEmail={KEILOT_MAIL_ADDRESS}
        keilotPhone={KEILOT_WHATSAPP_NUMBER}
        isCommunityManagementRole={isCommunityManagementRole}
      />
      <UpdatePopup
        open={showUpdatePopup}
        onClose={() => setShowUpdatePopup(false)}
        isCommunityManagementRole={isCommunityManagementRole}
      />
      <Box sx={root}>
        <CssBaseline />
        <TopAppBar
          isBelowMd={isBelowMd}
          drawerWidth={drawerWidth}
          toggleDrawer={toggleDrawer}
          pageTitle={pageTitle}
          currentItem={currentItem}
        />

        <Drawer
          sx={drawer(isBelowMd, drawerWidth)}
          variant={isBelowMd ? "temporary" : "persistent"}
          open={drawerOpen}
          anchor="left"
          onClose={closeDrawer}
          PaperProps={{
            className: !isBelowMd ? "custom-scrollbar" : "",
          }}
        >
          {isBelowMd && (
            <Box sx={closeButtonContainer}>
              <IconButton onClick={closeDrawer}>
                <CloseIcon sx={closeIcon} />
              </IconButton>
            </Box>
          )}

          {/* Sidebar Section */}

          <Box sx={sidebarContent}>
            <SidebarHeader
              {...{ isCommunityManagementRole, sidebarData, userCommunities, isMoMView }}
            />

            <Box
              sx={{
                ...menuItemsContainer,
                ...(activeScreen !== "" && { display: "none" }),
              }}
            >
              {sidebarItems.map((item, index) => (
                <NavLink
                  to={item.link}
                  onClick={closeDrawer}
                  key={index}
                  style={{
                    textDecoration: "none",
                    pointerEvents: item.disabled ? "none" : "auto",
                  }}
                >
                  {({ isActive }) => (
                    <ListItem
                      sx={{
                        ...menuItem(isActive),
                        opacity: item.disabled ? 0.5 : 1,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: "auto" }}>
                        <img
                          src={item.iconPath}
                          alt=""
                          style={{ width: "20px", height: "20px" }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={t(item.text + ".menu_item")}
                        // A long label next to a badge (e.g. English "Grow your
                        // community" + "Preview") wraps instead of running
                        // under the badge.
                        sx={
                          item.disabled || item.preview
                            ? { ...menuItemText, whiteSpace: "normal", minWidth: 0 }
                            : menuItemText
                        }
                      />
                      {item.disabled && (
                        <Box sx={{ ...soonBadge, flexShrink: 0 }}>
                          {t("bottom-menu-and-settings:soon")}
                        </Box>
                      )}
                      {item.preview && (
                        <Box sx={{ ...previewBadge, flexShrink: 0 }}>
                          {t("bottom-menu-and-settings:preview")}
                        </Box>
                      )}
                    </ListItem>
                  )}
                </NavLink>
              ))}
            </Box>

            <Box
              sx={{
                ...bottomNavContainer,
                ...(activeScreen !== "" && { mt: 0 }),
              }}
            >
              <BottomNavMenu
                {...{
                  isBelowMd,
                  handleItemClick,
                  activeScreen,
                  setActiveScreen,
                  isCommunityManagementRole,
                  isMoMView,
                  sidebarData,
                  closeDrawer,
                  languages,
                  onLanguageChange: updateLanguage,
                  getStartedContent,
                  fetchGetStartedContent,
                  tosUpdateDialog,
                  setTosUpdateDialog,
                  remainingTosUpdates,
                  setRemainingTosUpdates,
                }}
              />
            </Box>
          </Box>
        </Drawer>

        <Box component="main" sx={mainContent(drawerOpen, drawerWidth, theme)}>
          <Box
            sx={{
              ...mainContentBox(isBelowMd),
              paddingBottom: [
                "/my-members",
                "/my-suppliers",
                "/my-communities",
                "/manage-pricelists",
                "/my-jobs",
              ].includes(location.pathname)
                ? "0px"
                : isBelowMd
                  ? "8px"
                  : "24px",
            }}
          >
            {!hideTitle && !mobileTitleOnly && (
              <Box
                sx={{
                  ...pageTitleBox(isBelowMd),

                  mb: ["/find-suppliers", "/find-communities"].includes(
                    location.pathname,
                  )
                    ? "27px"
                    : ["/my-jobs"].includes(location.pathname)
                      ? "16px"
                      : "40px",
                }}
              >
                <Box>
                  <Box sx={pageTitleTextBox}>
                    <Typography sx={pageTitleStyle}>
                      {pageTitle}{" "}
                      <Typography as="span" id="header-sub-title"></Typography>
                    </Typography>

                    {currentItem?.preview && (
                      <Box component="span" sx={previewBadge}>
                        {t("bottom-menu-and-settings:preview_only")}
                      </Box>
                    )}
                  </Box>
                </Box>

                <Box id="header-button-box"></Box>
                {currentItem?.showTooltip && (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CustomTooltipBox
                      sections={t(currentItem.id, {
                        returnObjects: true,
                        ns: "tooltip-sections",
                      })}
                    />
                  </Box>
                )}
              </Box>
            )}
            <Box sx={contentContainer}>{children}</Box>
          </Box>
        </Box>
      </Box>
      {/* The chat assistant: renders nothing unless it's on for this person. */}
      <AgentChatBoundary>
        <AgentChat />
      </AgentChatBoundary>
      <UpdateTos
        {...{
          tosUpdateDialog,
          remainingTosUpdates,
          setRemainingTosUpdates,
          setTosUpdateDialog,
        }}
      />
    </>
  );
};

export default CommunityLayout;
